import io, { ManagerOptions, Socket, SocketOptions } from 'socket.io-client'

export abstract class BaseSocket {
  protected abstract _buildConfig(): Partial<ManagerOptions & SocketOptions>
  private _socket: Socket
  private _path: string

  private _eventsListening: [string, (data: any) => void][] = []

  public get Socket() {
    if (!this._socket) {
      this._build()
    }

    return this._socket
  }

  constructor(path: string) {
    this._path = path
    this._socket = io(this._path, this._buildConfig())
  }

  protected _build() {
    this._socket = io(this._path, this._buildConfig())
    this._eventsListening = []
  }

  public close() {
    this._socket?.close()
    this._socket = null
  }

  protected _findExistedEvents(event: string) {
    return this._eventsListening.filter(([eventName]) => eventName === event)
  }

  protected _getRunningEvent(event: string, handler: (data: any) => void) {
    return {
      off: () => {
        if (this._socket) this.off(event, handler)
      },
    }
  }

  public on(event: string, handler: (data: any) => void) {
    const existedEventsListening = this._findExistedEvents(event)
    const isHandlerDeclared = existedEventsListening.find(([_, existHandler]) => existHandler === handler)

    if (isHandlerDeclared) {
      return this._getRunningEvent(event, handler)
    }

    if (existedEventsListening.length > 0) {
      const compiledHandler = (data: any) => {
        existedEventsListening.forEach(([_, existHandler]) => {
          existHandler(data)
        })

        handler(data)
      }

      this.Socket.off(event)
      this.Socket.on(event, compiledHandler)
    } else {
      this.Socket.on(event, handler)
    }

    this._eventsListening.push([event, handler])

    return this._getRunningEvent(event, handler)
  }

  public off(event: string, handler: (data: any) => void) {
    const existedEventsListening = this._findExistedEvents(event)
    const isHandlerDeclared = existedEventsListening.find(([_, existHandler]) => existHandler === handler)
    if (!isHandlerDeclared) {
      throw new Error('This socket event is not openned before close')
    }

    if (existedEventsListening.length > 1) {
      const compiledHandler = (data: any) => {
        existedEventsListening.forEach(([_, existHandler]) => {
          if (existHandler == handler) return
          existHandler(data)
        })
      }

      this.Socket.off(event)
      this.Socket.on(event, compiledHandler)
    } else {
      this.Socket.off(event)
    }

    this._eventsListening = this._eventsListening.filter(([eventName, existHandler]) => {
      return !(eventName === event && existHandler === handler)
    })

    if (this._eventsListening.length <= 0) {
      this.close()
    }

    return true
  }
}
